import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapPin, Calendar, Users, Search, Star, ChevronRight, Wifi, Car, Utensils, Dumbbell, Coffee } from "lucide-react";
import "./BookingPageNew.css";

/*  helpers  */
function scoreLabel(score) {
  if (score >= 9.5) return "Exceptional";
  if (score >= 9)   return "Superb";
  if (score >= 8)   return "Excellent";
  if (score >= 7)   return "Very Good";
  return "Good";
}

function scoreBg(score) {
  if (score >= 9) return "#003580";
  if (score >= 8) return "#0071c2";
  return "#6b7280";
}

function nightsBetween(from, to) {
  if (!from || !to) return 1;
  const diff = new Date(to) - new Date(from);
  return Math.max(1, Math.round(diff / 86400000));
}

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

function formatDate(str) {
  if (!str) return "";
  return new Date(str).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

const HOTEL_IMAGES = [
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80",
  "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&q=80",
  "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80",
  "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=600&q=80",
  "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&q=80",
  "https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=600&q=80",
];

/*  component  */
export default function BookingPageNew() {
  const navigate  = useNavigate();
  const location  = useLocation();

  /*  read URL search params  */
  const qp = new URLSearchParams(location.search);

  const [dest,     setDest]     = useState(qp.get("destination") || "");
  const [checkIn,  setCheckIn]  = useState(qp.get("checkIn")  || "");
  const [checkOut, setCheckOut] = useState(qp.get("checkOut") || "");
  const [guests,   setGuests]   = useState(Number(qp.get("guests")) || 2);

  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [filterBreakfast,       setFilterBreakfast]       = useState(false);
  const [filterFreeCancellation,setFilterFreeCancellation] = useState(false);
  const [filterNoPrePayment,    setFilterNoPrePayment]     = useState(false);
  const [sortBy,   setSortBy]   = useState("recommended");

  const [hotels,  setHotels]  = useState([]);
  const [loading, setLoading] = useState(true);

  /*  default dates if missing  */
  useEffect(() => {
    if (!checkIn || !checkOut) {
      const d1 = new Date(); d1.setDate(d1.getDate() + 1);
      const d2 = new Date(); d2.setDate(d2.getDate() + 2);
      const fmt2 = d => d.toISOString().split("T")[0];
      if (!checkIn)  setCheckIn(fmt2(d1));
      if (!checkOut) setCheckOut(fmt2(d2));
    }
  }, []);

  /*  fetch  */
  useEffect(() => { fetchHotels(); }, [dest, minPrice, maxPrice, guests]);

  async function fetchHotels() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: "active", limit: 50 });
      if (dest)     params.append("search",    dest);
      if (minPrice) params.append("price_min", minPrice);
      if (maxPrice) params.append("price_max", maxPrice);
      if (guests)   params.append("capacity",  guests);

      const res  = await fetch(`/api/hotels?${params}`);
      const data = await res.json();

      const rows = data.roomListings || [];
      const map  = new Map();

      rows.forEach((r, idx) => {
        if (!map.has(r.hotel_id)) {
          map.set(r.hotel_id, {
            hotelId:            r.hotel_id,
            hotelName:          r.hotel_name,
            city:               r.city   || "",
            state:              r.state  || "",
            country:            r.country || "",
            rating:             parseFloat(r.rating)      || 8.5,
            reviewCount:        parseInt(r.review_count)  || 0,
            isFeatured:         r.is_featured             || false,
            distanceFromCenter: r.distance_from_center    || null,
            image:              HOTEL_IMAGES[idx % HOTEL_IMAGES.length],
            minPrice:           parseFloat(r.price)       || 0,
            roomTypes:          [],
          });
        }
        const h = map.get(r.hotel_id);
        h.roomTypes.push({
          id:              r.room_type_id,
          name:            r.room_type,
          capacity:        r.capacity        || 0,
          price:           parseFloat(r.price)          || 0,
          originalPrice:   r.original_price ? parseFloat(r.original_price) : null,
          breakfastIncluded: r.breakfast_included || false,
          freeCancellation:  r.free_cancellation  || false,
          noPrePayment:      r.no_prepayment       || false,
          bedInfo:           r.bed_info            || null,
          roomSizeSqm:       r.room_size_sqm       || null,
          roomTypeDetail:    r.room_type_detail     || null,
          available:         r.available_rooms_count || 0,
        });
        h.minPrice = Math.min(h.minPrice, parseFloat(r.price) || 0);
      });

      setHotels(Array.from(map.values()));
    } catch (e) {
      console.error(e);
      setHotels([]);
    } finally {
      setLoading(false);
    }
  }

  /*  filter + sort  */
  const nights = nightsBetween(checkIn, checkOut);

  const displayed = hotels
    .filter(h => {
      if (filterBreakfast && !h.roomTypes.some(r => r.breakfastIncluded))         return false;
      if (filterFreeCancellation && !h.roomTypes.some(r => r.freeCancellation))   return false;
      if (filterNoPrePayment && !h.roomTypes.some(r => r.noPrePayment))           return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "price-asc")   return a.minPrice - b.minPrice;
      if (sortBy === "price-desc")  return b.minPrice - a.minPrice;
      if (sortBy === "rating")      return b.rating - a.rating;
      return 0;
    });

  /*  search handler  */
  function handleSearch() {
    fetchHotels();
  }

  /*  render  */
  return (
    <div className="bpn-page">

      {/*  Search Hero  */}
      <div className="bpn-hero">
        <div className="bpn-hero-inner">
          <h1 className="bpn-hero-title">Find your perfect stay</h1>
          <div className="bpn-search-bar">
            {/* Destination */}
            <div className="bpn-sb-field bpn-sb-dest">
              <MapPin size={18} className="bpn-sb-icon" />
              <input
                type="text"
                placeholder="Where are you going?"
                value={dest}
                onChange={e => setDest(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
              />
            </div>
            {/* Check-in */}
            <div className="bpn-sb-field bpn-sb-date">
              <Calendar size={18} className="bpn-sb-icon" />
              <div className="bpn-sb-date-pair">
                <input
                  type="date"
                  value={checkIn}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={e => setCheckIn(e.target.value)}
                />
                <span className="bpn-sb-date-arrow">&#x2192;</span>
                <input
                  type="date"
                  value={checkOut}
                  min={checkIn}
                  onChange={e => setCheckOut(e.target.value)}
                />
              </div>
            </div>
            {/* Guests */}
            <div className="bpn-sb-field bpn-sb-guests">
              <Users size={18} className="bpn-sb-icon" />
              <div className="bpn-guests-row">
                <button onClick={() => setGuests(g => Math.max(1, g - 1))}>&#x2212;</button>
                <span>{guests} adult{guests !== 1 ? "s" : ""}</span>
                <button onClick={() => setGuests(g => g + 1)}>+</button>
              </div>
            </div>
            {/* Button */}
            <button className="bpn-search-btn" onClick={handleSearch}>
              <Search size={18} />
              Search
            </button>
          </div>
        </div>
      </div>

      {/*  Layout  */}
      <div className="bpn-layout">

        {/*  Sidebar  */}
        <aside className="bpn-sidebar">
          <h3 className="bpn-sidebar-title">Filter by:</h3>

          {/* Budget */}
          <div className="bpn-filter-block">
            <p className="bpn-filter-label">Your budget (per night)</p>
            <div className="bpn-price-row">
              <input type="number" placeholder="Min" value={minPrice} onChange={e => setMinPrice(e.target.value)} />
              <span>&#8211;</span>
              <input type="number" placeholder="Max" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} />
            </div>
          </div>

          {/* Popular filters */}
          <div className="bpn-filter-block">
            <p className="bpn-filter-label">Popular filters</p>
            <label className="bpn-chk">
              <input type="checkbox" checked={filterBreakfast} onChange={e => setFilterBreakfast(e.target.checked)} />
              Breakfast included
            </label>
            <label className="bpn-chk">
              <input type="checkbox" checked={filterFreeCancellation} onChange={e => setFilterFreeCancellation(e.target.checked)} />
              Free cancellation
            </label>
            <label className="bpn-chk">
              <input type="checkbox" checked={filterNoPrePayment} onChange={e => setFilterNoPrePayment(e.target.checked)} />
              No prepayment needed
            </label>
          </div>

          {/* Facilities */}
          <div className="bpn-filter-block">
            <p className="bpn-filter-label">Facilities</p>
            {[
              { icon: <Wifi size={14}/>,      label: "Free WiFi" },
              { icon: <Car size={14}/>,       label: "Free parking" },
              { icon: <Utensils size={14}/>,  label: "Restaurant" },
              { icon: <Dumbbell size={14}/>,  label: "Fitness centre" },
              { icon: <Coffee size={14}/>,    label: "Room service" },
            ].map(f => (
              <label key={f.label} className="bpn-chk">
                <input type="checkbox" />
                <span className="bpn-chk-icon">{f.icon}</span>
                {f.label}
              </label>
            ))}
          </div>
        </aside>

        {/*  Results  */}
        <main className="bpn-results">
          <div className="bpn-results-header">
            <div>
              <h2 className="bpn-results-title">
                {dest ? `${dest}: ` : "All destinations: "}
                <span>{displayed.length} propert{displayed.length !== 1 ? "ies" : "y"} found</span>
              </h2>
              {(checkIn && checkOut) && (
                <p className="bpn-results-dates">
                  {formatDate(checkIn)} &ndash; {formatDate(checkOut)} &middot; {nights} night{nights !== 1 ? "s" : ""}
                </p>
              )}
            </div>
            <div className="bpn-sort-row">
              <label>Sort by:</label>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="recommended">Our top picks</option>
                <option value="rating">Best reviewed</option>
                <option value="price-asc">Price (lowest first)</option>
                <option value="price-desc">Price (highest first)</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="bpn-loading">
              <div className="bpn-spinner" />
              <p>Finding the best properties...</p>
            </div>
          ) : displayed.length === 0 ? (
            <div className="bpn-empty">
              <Search size={56} strokeWidth={1.2} />
              <h3>No properties found</h3>
              <p>Try adjusting your search or removing some filters.</p>
            </div>
          ) : (
            <div className="bpn-card-list">
              {displayed.map((hotel, idx) => {
                const rt = hotel.roomTypes.sort((a, b) => a.price - b.price)[0];
                const location2 = [hotel.city, hotel.state, hotel.country].filter(Boolean).join(", ");
                const ratingScore = hotel.rating.toFixed(1);
                const label = scoreLabel(hotel.rating);
                const bg    = scoreBg(hotel.rating);
                const totalPrice = (rt?.price || hotel.minPrice) * nights;
                const origTotal  = rt?.originalPrice ? rt.originalPrice * nights : null;
                const discount   = origTotal ? Math.round(100 - (totalPrice / origTotal) * 100) : 0;

                return (
                  <div key={hotel.hotelId} className="bpn-card" onClick={() => navigate(`/property/${hotel.hotelId}`)}>
                    {/* Left  image */}
                    <div className="bpn-card-img-wrap">
                      <img className="bpn-card-img" src={hotel.image} alt={hotel.hotelName} loading="lazy" />
                      {hotel.isFeatured && <span className="bpn-card-featured">Featured</span>}
                      {discount > 0 && <span className="bpn-card-deal">{discount}% today</span>}
                    </div>

                    {/* Center  info */}
                    <div className="bpn-card-body">
                      <div className="bpn-card-name-row">
                        <h3 className="bpn-card-name">{hotel.hotelName}</h3>
                        {hotel.isFeatured && (
                          <span className="bpn-badge-featured">
                            <Star size={11} fill="#febb02" color="#febb02" /> Featured
                          </span>
                        )}
                      </div>

                      <div className="bpn-card-loc">
                        <MapPin size={13} />
                        <span>{location2}</span>
                        {hotel.distanceFromCenter && (
                          <span className="bpn-card-dist"> . {hotel.distanceFromCenter} from centre</span>
                        )}
                        <span className="bpn-card-map-link">Show on map</span>
                      </div>

                      {/* Room block */}
                      {rt && (
                        <div className="bpn-room-block">
                          <p className="bpn-room-name">{rt.name}</p>
                          <p className="bpn-room-detail">
                              {[rt.roomTypeDetail, rt.bedInfo, rt.roomSizeSqm ? `${rt.roomSizeSqm} m2` : null]
                                .filter(Boolean).join(" \u00B7 ")}
                          </p>

                          <div className="bpn-room-perks">
                            {rt.breakfastIncluded && (
                              <span className="bpn-perk bpn-perk-green">&#10003; Breakfast included</span>
                            )}
                            {rt.freeCancellation && (
                              <span className="bpn-perk bpn-perk-green">&#10003; Free cancellation</span>
                            )}
                            {rt.noPrePayment && (
                              <span className="bpn-perk bpn-perk-dark">&#10003; No prepayment needed - pay at the property</span>
                            )}
                          </div>

                          {rt.available <= 3 && rt.available > 0 && (
                            <p className="bpn-limited">
                              Only {rt.available} left at this price on our site
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right  score + price + CTA */}
                    <div className="bpn-card-price">
                      {/* Score */}
                      <div className="bpn-score-row">
                        <div className="bpn-score-text">
                          <span className="bpn-score-label">{label}</span>
                          {hotel.reviewCount > 0 && (
                            <span className="bpn-review-count">{hotel.reviewCount.toLocaleString()} reviews</span>
                          )}
                        </div>
                        <div className="bpn-score-badge" style={{ background: bg }}>
                          {ratingScore}
                        </div>
                      </div>

                      <div className="bpn-price-block">
                        {(checkIn && checkOut) && (
                          <p className="bpn-nights-line">
                            {formatDate(checkIn)} &ndash; {formatDate(checkOut)}, {nights} night{nights !== 1 ? "s" : ""}
                          </p>
                        )}
                        {origTotal && (
                          <p className="bpn-orig-price">PKR {fmt(origTotal)}</p>
                        )}
                        <p className="bpn-final-price">PKR {fmt(totalPrice)}</p>
                        <p className="bpn-taxes">Includes taxes &amp; fees</p>
                      </div>

                      <button
                        className="bpn-see-avail-btn"
                        onClick={e => { e.stopPropagation(); navigate(`/property/${hotel.hotelId}`); }}
                      >
                        See availability <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

